import { Keys, ThemeMode } from '@eih-netbroker/common';
import { darkThemeClass, lightThemeClass } from '@eih-netbroker/web-common-ui';
import { ThemeProvider } from '@mui/material';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { collectColors } from '../generated/collectColors';
import { ColorsType } from '../generated/ColorsType';
import { themeUtils } from '../shared/utils/themeUtils';
import { useLocalStorage } from '../shared/utils/useLocalStorage';
import { isWindowsOrLinux } from '../shared/utils/userAgent';

declare module '@mui/material/styles' {
  interface Palette {
    emphasize: Palette['primary'];
  }

  interface PaletteOptions {
    emphasize: PaletteOptions['primary'];
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    emphasize: true;
  }
}

type NbThemeContextProps =
  | undefined
  | {
      rem: (fullHdSizeInPx: number) => number;
      isDark: boolean;
      themeMode: ThemeMode;
      setTheme: (themeMode: ThemeMode) => void;
      isDynamicSizingEnabled: boolean;
      setDynamicResolutionEnabled: (isDynamicResolutionEnabled: boolean) => void;
      colors: ColorsType;
    };

const NbThemeContext = createContext<NbThemeContextProps>(undefined);

export const NbThemeProvider = ({ children }: { children: ReactNode }) => {
  const [colors, setColors] = useState<ColorsType>(() => collectColors(document.documentElement));
  const [baseFontSize, setBaseFontSize] = useState<number>(themeUtils.getBaseFontSize(window.innerWidth));
  const { isDynamicSizingEnabled, setDynamicResolutionEnabled } = useLocalStorage();
  const [themeMode, setThemeMode] = useState<ThemeMode>(themeUtils.initialThemeMode);

  const onWindowResize = useCallback(() => {
    const width = window.innerWidth;
    setBaseFontSize(themeUtils.getBaseFontSize(width));
  }, [setBaseFontSize]);

  const rem = useCallback(
    (fullHdSizeInPx: number): number => {
      if (!isDynamicSizingEnabled || baseFontSize === themeUtils.DefaultBaseFontSize) {
        return fullHdSizeInPx;
      }

      return (fullHdSizeInPx * baseFontSize) / themeUtils.DefaultBaseFontSize;
    },
    [baseFontSize, isDynamicSizingEnabled],
  );

  const isDark = useMemo(() => {
    return themeUtils.isThemeDark(themeMode);
  }, [themeMode]);

  const muiTheme = useMemo(() => {
    if (isDark) {
      return themeUtils.darkTheme;
    } else {
      return themeUtils.lightTheme;
    }
  }, [isDark]);

  const updateThemeClasses = useCallback((pThemeMode: ThemeMode) => {
    const isDarkTheme = themeUtils.isThemeDark(pThemeMode);
    document.documentElement.classList.toggle(Keys.Theme.light, !isDarkTheme);
    document.documentElement.classList.toggle(Keys.Theme.dark, isDarkTheme);
    document.documentElement.classList.toggle(lightThemeClass, !isDarkTheme);
    document.documentElement.classList.toggle(darkThemeClass, isDarkTheme);
    setColors(collectColors(document.documentElement));
  }, []);

  const setTheme = useCallback(
    (pThemeMode: ThemeMode) => {
      setThemeMode(pThemeMode);
      window.localStorage.setItem(Keys.Theme.key, pThemeMode);
      updateThemeClasses(pThemeMode);
    },
    [updateThemeClasses],
  );

  useEffect(() => {
    updateThemeClasses(themeMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- this useEffect should only handle the initial page load
  }, []);

  useEffect(() => {
    if (isDynamicSizingEnabled) {
      window.addEventListener('resize', onWindowResize);
      document.querySelector(`html`)!.classList.add(`dynamicSizingEnabled`);
    } else {
      document.querySelector(`html`)!.classList.remove(`dynamicSizingEnabled`);
    }

    return () => {
      window.removeEventListener('resize', onWindowResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onWindowResize doesn't need to be added here, because we don't want constant readd of that event listener
  }, [isDynamicSizingEnabled]);

  useEffect(() => {
    const needCustomScrollbar = isWindowsOrLinux();

    if (needCustomScrollbar) {
      document.querySelector(`html`)!.classList.add(`customScrollbar`);
    }
  }, []);

  return (
    <NbThemeContext.Provider
      value={{ rem, isDark, themeMode, setTheme, isDynamicSizingEnabled, setDynamicResolutionEnabled, colors }}
    >
      <ThemeProvider theme={muiTheme}>{children}</ThemeProvider>
    </NbThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(NbThemeContext);
  if (context === undefined) {
    throw new Error(`useTheme must be used within a NbThemeProvider`);
  }
  return context;
};
